import { CircularProgress, Chip, Stack, Tooltip, Typography } from "@mui/material";
import {
    CheckCircleOutlineOutlined,
    ErrorOutlineOutlined,
    InfoOutlined,
    WarningAmberOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { DirectdIndicatorType } from "../types/types";

type DirectdIndicatorsProps = {
    loading: boolean;
    indicators: Array<DirectdIndicatorType> | null;
    consultedAt?: string | null;
    stale?: boolean;
    error?: string | null;
};

const severityProps = {
    critical: { color: "error" as const, icon: <ErrorOutlineOutlined /> },
    warning: { color: "warning" as const, icon: <WarningAmberOutlined /> },
    info: { color: "info" as const, icon: <InfoOutlined /> },
};

/** Chips com os indicadores da consulta Direct Data (placa ou CNH) exibidos no formulario. */
const DirectdIndicators = ({ loading, indicators, consultedAt, stale, error }: DirectdIndicatorsProps) => {
    if (loading) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">Consultando dados...</Typography>
            </Stack>
        );
    }

    if (error) {
        return <Typography variant="caption" color="text.secondary">{error}</Typography>;
    }

    if (!indicators) return null;

    const consultedLabel = consultedAt
        ? `Consultado em ${dayjs(consultedAt).format("DD/MM/YYYY HH:mm")}${stale ? " (desatualizado: a consulta falhou)" : ""}`
        : null;

    return (
        <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {indicators.length === 0 && (
                    <Chip
                        size="small"
                        variant="outlined"
                        color="success"
                        icon={<CheckCircleOutlineOutlined />}
                        label="Nenhuma pendência encontrada"
                    />
                )}
                {indicators.map((indicator, index) => {
                    const chip = (
                        <Chip
                            key={`${indicator.code}-${index}`}
                            size="small"
                            variant="outlined"
                            color={severityProps[indicator.severity].color}
                            icon={severityProps[indicator.severity].icon}
                            label={indicator.label}
                        />
                    );
                    return indicator.detail ? (
                        <Tooltip key={`${indicator.code}-${index}`} title={indicator.detail} arrow>
                            {chip}
                        </Tooltip>
                    ) : chip;
                })}
            </Stack>
            {consultedLabel && (
                <Typography variant="caption" color="text.secondary">{consultedLabel}</Typography>
            )}
        </Stack>
    );
};

export default DirectdIndicators;
